import React, { useEffect, useRef, useState } from "react";
import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  Bold,
  Heading1,
  Heading2,
  Italic,
  Link,
  List,
  ListOrdered,
  Redo2,
  RemoveFormatting,
  Undo2,
  Underline,
} from "lucide-react";

interface Props {
  value: string;
  onChange: (value: string) => void;
}

const buttonClass =
  "flex h-9 w-9 items-center justify-center rounded-md border border-slate-700 text-slate-300 transition hover:bg-slate-800 hover:text-white";

export default function RichTextEditor({ value, onChange }: Props) {
  const editorRef = useRef<HTMLDivElement | null>(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    const editor = editorRef.current;
    if (editor && editor.innerHTML !== value) {
      editor.innerHTML = value || "";
    }
  }, [value]);

  const runCommand = (command: string, commandValue?: string) => {
    editorRef.current?.focus();
    window.document.execCommand(command, false, commandValue);
    onChange(editorRef.current?.innerHTML || "");
  };

  const setBlock = (blockName: string) => {
    editorRef.current?.focus();
    const normalizedBlock = blockName.toLowerCase();
    window.document.execCommand("formatBlock", false, normalizedBlock);

    const selection = window.getSelection();
    const anchorElement = selection?.anchorNode?.nodeType === Node.TEXT_NODE
      ? selection.anchorNode.parentElement
      : selection?.anchorNode as HTMLElement | null;

    if (anchorElement && normalizedBlock !== "p") {
      const currentBlock = anchorElement.closest("h1,h2,p");
      if (!currentBlock || currentBlock.tagName.toLowerCase() !== normalizedBlock) {
        window.document.execCommand("formatBlock", false, `<${normalizedBlock}>`);
      }
    }

    onChange(editorRef.current?.innerHTML || "");
  };

  const addLink = () => {
    const url = window.prompt("Enter link URL");
    if (!url) return;
    const normalized = /^https?:\/\//i.test(url) || /^mailto:/i.test(url) ? url : `https://${url}`;
    runCommand("createLink", normalized);
  };

  return (
    <div className={`rounded-lg border bg-slate-950 transition ${isFocused ? "border-blue-500 ring-2 ring-blue-500/20" : "border-slate-700"}`}>
      <div className="flex flex-wrap gap-2 border-b border-slate-800 p-2">
        <button type="button" className={buttonClass} title="Heading 1" onMouseDown={(event) => { event.preventDefault(); setBlock("h1"); }}><Heading1 size={16} /></button>
        <button type="button" className={buttonClass} title="Heading 2" onMouseDown={(event) => { event.preventDefault(); setBlock("h2"); }}><Heading2 size={16} /></button>
        <button type="button" className={buttonClass} title="Paragraph" onMouseDown={(event) => { event.preventDefault(); setBlock("p"); }}>P</button>
        <span className="mx-1 h-9 w-px bg-slate-800" />
        <button type="button" className={buttonClass} title="Bold" onMouseDown={(event) => { event.preventDefault(); runCommand("bold"); }}><Bold size={16} /></button>
        <button type="button" className={buttonClass} title="Italic" onMouseDown={(event) => { event.preventDefault(); runCommand("italic"); }}><Italic size={16} /></button>
        <button type="button" className={buttonClass} title="Underline" onMouseDown={(event) => { event.preventDefault(); runCommand("underline"); }}><Underline size={16} /></button>
        <button type="button" className={buttonClass} title="Clear formatting" onMouseDown={(event) => { event.preventDefault(); runCommand("removeFormat"); }}><RemoveFormatting size={16} /></button>
        <span className="mx-1 h-9 w-px bg-slate-800" />
        <button type="button" className={buttonClass} title="Bullet list" onMouseDown={(event) => { event.preventDefault(); runCommand("insertUnorderedList"); }}><List size={16} /></button>
        <button type="button" className={buttonClass} title="Numbered list" onMouseDown={(event) => { event.preventDefault(); runCommand("insertOrderedList"); }}><ListOrdered size={16} /></button>
        <button type="button" className={buttonClass} title="Link" onMouseDown={(event) => { event.preventDefault(); addLink(); }}><Link size={16} /></button>
        <span className="mx-1 h-9 w-px bg-slate-800" />
        <button type="button" className={buttonClass} title="Align left" onMouseDown={(event) => { event.preventDefault(); runCommand("justifyLeft"); }}><AlignLeft size={16} /></button>
        <button type="button" className={buttonClass} title="Align center" onMouseDown={(event) => { event.preventDefault(); runCommand("justifyCenter"); }}><AlignCenter size={16} /></button>
        <button type="button" className={buttonClass} title="Align right" onMouseDown={(event) => { event.preventDefault(); runCommand("justifyRight"); }}><AlignRight size={16} /></button>
        <span className="mx-1 h-9 w-px bg-slate-800" />
        <button type="button" className={buttonClass} title="Undo" onMouseDown={(event) => { event.preventDefault(); runCommand("undo"); }}><Undo2 size={16} /></button>
        <button type="button" className={buttonClass} title="Redo" onMouseDown={(event) => { event.preventDefault(); runCommand("redo"); }}><Redo2 size={16} /></button>
      </div>

      <div className="bg-slate-950 p-3">
        <div
          ref={editorRef}
          contentEditable
          suppressContentEditableWarning
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          onInput={(event) => onChange((event.currentTarget as HTMLDivElement).innerHTML)}
          className="min-h-[360px] rounded-md bg-white px-6 py-5 text-[15px] leading-7 text-slate-950 outline-none [&_a]:text-blue-700 [&_a]:underline [&_h1]:mb-3 [&_h1]:text-3xl [&_h1]:font-bold [&_h2]:mb-2 [&_h2]:text-2xl [&_h2]:font-bold [&_h3]:text-xl [&_h3]:font-bold [&_li]:ml-5 [&_ol]:list-decimal [&_p]:mb-3 [&_ul]:list-disc"
        />
      </div>
    </div>
  );
}
